// src/features/scope_module/components/EquilibriumAnalysis.js
import React, { useMemo } from 'react';

// clamp to [0,1] and format to percentage text
const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));
const fmtPct = (v) => `${Math.round(clamp01(v) * 100)}%`;

// simple inline styles
const styles = {
  panel: { display: 'flex', flexDirection: 'column', gap: 10, padding: 10 },
  header: { fontWeight: 600, marginBottom: 6 },

  infoRow: { padding: 8, color: 'var(--text-secondary, #b0b0b0)' },

  content: { display: 'flex', flexDirection: 'column', gap: 10 },
  pairBlock: { display: 'flex', flexDirection: 'column', gap: 6 },

  labelsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: 'var(--text-secondary, #b0b0b0)'
  },

  barTrack: {
    position: 'relative',
    height: 10,
    background: '#111',
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid #333'
  },
  barFillLeft: { position: 'absolute', left: 0, top: 0, bottom: 0, background: '#3a86ff' },
  barFillRight: { position: 'absolute', right: 0, top: 0, bottom: 0, background: '#ff006e' },
  barThumb: { position: 'absolute', top: -3, width: 2, height: 16, background: '#ffd166', transform: 'translateX(-1px)' },

  stabilityRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 },
  stabilityLabel: { fontSize: 12, color: 'var(--text-secondary, #b0b0b0)' },
  stabilityTrack: { flex: 1, height: 6, borderRadius: 4, background: '#111', border: '1px solid #333' },
  stabilityFill: { height: '100%', borderRadius: 4, background: '#06d6a0' },

  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 8 }
};

export default function EquilibriumAnalysis({ metrics }) {
  // Normalize the three pairs and their stability metrics if present
  const pairs = useMemo(() => {
    if (!metrics) return [];
    const m = metrics;

    // Accept canonical names and common alternates/camelCase
    const ee =
      m.ee_ratio ??
      m.exploration_exploitation ??
      m.explorationVsExploitation ??
      m.ee ??
      null;

    const aa =
      m.aa_ratio ??
      m.assimilation_accommodation ??
      m.assimilationVsAccommodation ??
      m.aa ??
      null;

    const er =
      m.er_ratio ??
      m.efficiency_resilience ??
      m.efficiencyVsResilience ??
      m.er ??
      null;

    const eeStab =
      m.ee_stability ??
      m.exploration_exploitation_stability ??
      m.eeStability ??
      null;

    const aaStab =
      m.aa_stability ??
      m.assimilation_accommodation_stability ??
      m.aaStability ??
      null;

    const erStab =
      m.er_stability ??
      m.efficiency_resilience_stability ??
      m.erStability ??
      null;

    return [
      { id: 'ee', left: 'Exploration', right: 'Exploitation', ratio: ee, stability: eeStab },
      { id: 'aa', left: 'Assimilation', right: 'Accommodation', ratio: aa, stability: aaStab },
      { id: 'er', left: 'Efficiency', right: 'Resilience', ratio: er, stability: erStab },
    ];
  }, [metrics]);

  if (!metrics) {
    return (
      <div style={styles.infoRow}>
        No metrics yet. Click <strong>Analyze</strong>.
      </div>
    );
  }

  return (
    <div className="equilibrium-panel" style={styles.panel}>
      <div style={styles.header}>Dynamic Equilibrium</div>

      <div style={styles.content}>
        {pairs.map(({ id, left, right, ratio, stability }) => {
          const r = clamp01(ratio ?? 0.5);
          return (
            <div key={id} style={styles.pairBlock}>
              <div style={styles.labelsRow}>
                <span>{left}</span>
                <span>{right}</span>
              </div>

              <div style={styles.barTrack}>
                <div style={{ ...styles.barFillLeft, width: fmtPct(1 - r) }} />
                <div style={{ ...styles.barFillRight, width: fmtPct(r) }} />
                <div style={{ ...styles.barThumb, left: fmtPct(r) }} />
              </div>

              <div style={styles.stabilityRow}>
                <span style={styles.stabilityLabel}>Stability</span>
                <div style={styles.stabilityTrack}>
                  <div style={{ ...styles.stabilityFill, width: fmtPct(stability ?? 0) }} />
                </div>
              </div>
            </div>
          );
        })}

        <div style={styles.summaryRow}>
          <div>EE: {fmtPct(pairs[0]?.ratio ?? 0.5)}</div>
          <div>AA: {fmtPct(pairs[1]?.ratio ?? 0.5)}</div>
          <div>ER: {fmtPct(pairs[2]?.ratio ?? 0.5)}</div>
        </div>
      </div>
    </div>
  );
}
